import * as Astronomy from 'astronomy-engine';

/**
 * Human Design Ephemeris Module
 * Uses astronomy-engine for accurate geocentric ecliptic longitude calculations.
 *
 * Key API functions from astronomy-engine:
 * - SunPosition(date) → { elon } (geocentric ecliptic longitude of Sun)
 * - EclipticGeoMoon(date) → { lon } (geocentric ecliptic longitude of Moon)
 * - GeoVector(body, date, aberration) → Vector (geocentric J2000 equatorial)
 * - Ecliptic(vector) → { elon } (convert J2000 equatorial to ecliptic)
 * - MakeTime(date) → AstroTime
 */

// Convert timezone abbreviation to UTC offset in hours
const TIMEZONE_OFFSETS = {
  'UTC': 0, 'GMT': 0,
  'CET': 1, 'CEST': 2, 'MEZ': 1, 'MESZ': 2,
  'WET': 0, 'WEST': 1,
  'EET': 2, 'EEST': 3,
  'EST': -5, 'EDT': -4,
  'CST': -6, 'CDT': -5,
  'MST': -7, 'MDT': -6,
  'PST': -8, 'PDT': -7,
  'IST': 5.5, 'JST': 9, 'AEST': 10, 'AEDT': 11,
  'NZST': 12, 'NZDT': 13,
  'MSK': 3, 'ICT': 7, 'CST_CN': 8
};

/**
 * Create an AstroTime from birth data
 */
export function createAstroTime(dateStr, timeStr, timezone) {
  const offset = (TIMEZONE_OFFSETS[timezone] !== undefined) ? TIMEZONE_OFFSETS[timezone] : (parseFloat(timezone) || 0);
  const [year, month, day] = dateStr.split('-').map(Number);
  const [hours, minutes] = timeStr.split(':').map(Number);

  // Convert local time to UTC
  const utcDate = new Date(Date.UTC(year, month - 1, day, hours, minutes, 0));
  utcDate.setUTCHours(utcDate.getUTCHours() - offset);

  return Astronomy.MakeTime(utcDate);
}

/**
 * Get geocentric ecliptic longitude of the Sun
 */
function getSunLongitude(time) {
  const sunPos = Astronomy.SunPosition(time);
  return sunPos.elon;
}

/**
 * Get geocentric ecliptic longitude of the Moon
 */
function getMoonLongitude(time) {
  const moonEcl = Astronomy.EclipticGeoMoon(time);
  return moonEcl.lon;
}

/**
 * Get geocentric ecliptic longitude of a planet
 * Uses GeoVector → Ecliptic conversion
 */
function getPlanetLongitude(body, time) {
  const geoVec = Astronomy.GeoVector(body, time, true);
  const ecl = Astronomy.Ecliptic(geoVec);
  return ecl.elon;
}

/**
 * Calculate the mean longitude of the Moon's ascending node (North Node)
 * Standard formula from Meeus "Astronomical Algorithms"
 * Ω = 125.04452 - 1934.136261 * T + 0.0020708 * T² + T³/450000
 * where T = Julian centuries from J2000.0
 */
function getNorthNodeLongitude(time) {
  // Compute the TRUE (osculating) ascending node of the Moon.
  // Approach: find the Moon's orbital plane from its position + velocity,
  // then compute where that plane intersects the ecliptic.

  const dt = 0.001; // days for finite difference (≈86 seconds)

  // Get Moon ecliptic positions at time and time+dt
  const moonNow = Astronomy.EclipticGeoMoon(time);
  const timePlus = Astronomy.MakeTime(addDays(time, dt));
  const moonLater = Astronomy.EclipticGeoMoon(timePlus);

  // Convert ecliptic coordinates to Cartesian (using distance for proper 3D vectors)
  const dist1 = moonNow.dist;
  const lon1 = moonNow.lon * Math.PI / 180;
  const lat1 = moonNow.lat * Math.PI / 180;
  const dist2 = moonLater.dist;
  const lon2 = moonLater.lon * Math.PI / 180;
  const lat2 = moonLater.lat * Math.PI / 180;

  const x1 = dist1 * Math.cos(lat1) * Math.cos(lon1);
  const y1 = dist1 * Math.cos(lat1) * Math.sin(lon1);
  const z1 = dist1 * Math.sin(lat1);

  const x2 = dist2 * Math.cos(lat2) * Math.cos(lon2);
  const y2 = dist2 * Math.cos(lat2) * Math.sin(lon2);
  const z2 = dist2 * Math.sin(lat2);

  // Cross product r1 × r2 gives normal to the orbital plane
  const nx = y1 * z2 - z1 * y2;
  const ny = z1 * x2 - x1 * z2;
  const nz = x1 * y2 - y1 * x2;

  // The ascending node is where the orbital plane intersects the ecliptic plane.
  // Ecliptic normal = (0, 0, 1). Node line = ecliptic_normal × orbital_normal = (-ny, nx, 0).
  // Ascending node longitude = atan2(nx, -ny)
  let nodeLon = Math.atan2(nx, -ny) * 180 / Math.PI;
  nodeLon = ((nodeLon % 360) + 360) % 360;

  return nodeLon;
}

/**
 * Get all 13 planetary activations for a given moment
 * Returns: { Sun, Earth, Moon, NorthNode, SouthNode, Mercury, Venus, Mars, Jupiter, Saturn, Uranus, Neptune, Pluto }
 * Each with { longitude, name }
 */
export function get13Planets(time) {
  const result = {};

  // Sun (geocentric ecliptic longitude)
  const sunLon = getSunLongitude(time);
  result.Sun = { longitude: sunLon, name: 'Sonne' };

  // Earth (opposite Sun, +180°)
  result.Earth = { longitude: (sunLon + 180) % 360, name: 'Erde' };

  // Moon
  result.Moon = { longitude: getMoonLongitude(time), name: 'Mond' };

  // North Node (mean ascending node)
  const nnLon = getNorthNodeLongitude(time);
  result.NorthNode = { longitude: nnLon, name: 'Nordknoten' };

  // South Node (opposite North Node)
  result.SouthNode = { longitude: (nnLon + 180) % 360, name: 'Südknoten' };

  // Planets
  const planets = [
    ['Mercury', 'Merkur'],
    ['Venus', 'Venus'],
    ['Mars', 'Mars'],
    ['Jupiter', 'Jupiter'],
    ['Saturn', 'Saturn'],
    ['Uranus', 'Uranus'],
    ['Neptune', 'Neptun'],
    ['Pluto', 'Pluto']
  ];

  for (const [body, germanName] of planets) {
    try {
      result[body] = { longitude: getPlanetLongitude(body, time), name: germanName };
    } catch (err) {
      console.warn(`Could not calculate ${body}:`, err.message);
      result[body] = { longitude: 0, name: germanName };
    }
  }

  return result;
}

/**
 * Find the Design date: go backward in time until Sun longitude = birth Sun - 88°
 * Uses binary search for precision.
 */
export function findDesignDate(birthTime) {
  const birthSunLon = getSunLongitude(birthTime);

  // Target: 88° before birth Sun
  let targetLon = birthSunLon - 88;
  if (targetLon < 0) targetLon += 360;

  // The Sun moves roughly 1° per day, so 88° back ≈ 88 days
  // Start searching around 85 days back, narrow down

  // Phase 1: Coarse search (1-day steps) to find the rough window
  let lowDays = 80;   // earliest estimate
  let highDays = 100;  // latest estimate

  // Find the day range where the Sun crosses the target longitude
  for (let d = 80; d <= 100; d += 0.5) {
    const testTime = Astronomy.MakeTime(addDays(birthTime, -d));
    const testSunLon = getSunLongitude(testTime);
    const diff = angleDiff(testSunLon, targetLon);

    if (Math.abs(diff) < 2) {
      lowDays = d - 2;
      highDays = d + 2;
      break;
    }
  }

  // Phase 2: Binary search for precision (< 0.001°)
  for (let iteration = 0; iteration < 50; iteration++) {
    const midDays = (lowDays + highDays) / 2;
    const midTime = Astronomy.MakeTime(addDays(birthTime, -midDays));
    const midSunLon = getSunLongitude(midTime);
    const diff = angleDiff(midSunLon, targetLon);

    if (Math.abs(diff) < 0.0001) {
      return midTime; // Found it
    }

    // Sun moves forward in ecliptic longitude over time
    // If midSunLon is "ahead" of target (positive diff), we need to go further back
    if (diff > 0) {
      lowDays = midDays;
    } else {
      highDays = midDays;
    }
  }

  // Return best estimate
  const bestDays = (lowDays + highDays) / 2;
  return Astronomy.MakeTime(addDays(birthTime, -bestDays));
}

/**
 * Calculate signed angular difference (a - b), normalized to [-180, +180]
 */
function angleDiff(a, b) {
  let diff = a - b;
  while (diff > 180) diff -= 360;
  while (diff < -180) diff += 360;
  return diff;
}

/**
 * Add days to a date (returns a JS Date)
 */
function addDays(astroTime, days) {
  // astroTime.date is the JS Date object
  const ms = astroTime.date.getTime() + days * 86400000;
  return new Date(ms);
}

/**
 * Get both Personality (birth) and Design (88° arc) planet positions
 */
export function getPersonalityAndDesign(birthTime) {
  const personality = get13Planets(birthTime);
  const designTime = findDesignDate(birthTime);
  const design = get13Planets(designTime);

  return {
    personality,
    design,
    birthTime,
    designTime
  };
}

/**
 * Format an AstroTime as a readable date string
 */
export function formatAstroTime(time) {
  if (!time || !time.date) return 'N/A';
  const d = time.date;
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  const hours = String(d.getUTCHours()).padStart(2, '0');
  const mins = String(d.getUTCMinutes()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${mins} UTC`;
}
